import logger from './logger';
import { isRestaurantMealPeriodActive } from './menuUnification.utils';
import { EXPRESS_MODE_MEAL_PERIOD_PREFIX } from './menuUnification.constants';
import { IRestaurantMealPeriod } from '../types/brandMenu';

/**
 * Detect whether the current restaurant has any "express mode" meal period
 * active right now. Express-mode meal periods are named with a prefix of
 * "express mode" (e.g. "express mode evening", "express mode lunch") and
 * may not be assigned to any item or constraint — so we scan the meal
 * periods map directly. The map is already filtered to the meal periods
 * that apply to the current restaurant (via restaurant_group membership).
 */
export const getExpressModePeriods = (
  restaurantMealPeriodsByName?: Record<string, IRestaurantMealPeriod>
): IRestaurantMealPeriod[] =>
  Object.values(restaurantMealPeriodsByName ?? {}).filter((mp) =>
    mp?.name?.toLowerCase().startsWith(EXPRESS_MODE_MEAL_PERIOD_PREFIX)
  );

export const hasExpressModePeriods = (
  restaurantMealPeriodsByName?: Record<string, IRestaurantMealPeriod>
): boolean => getExpressModePeriods(restaurantMealPeriodsByName).length > 0;

export const isExpressModeActive = (
  restaurantMealPeriodsByName?: Record<string, IRestaurantMealPeriod>,
  timezone?: string
): boolean => {
  if (!restaurantMealPeriodsByName || !timezone) {
    logger.log(
      `[EXPRESS_MODE] inputs missing — restaurantMealPeriodsByName=${!!restaurantMealPeriodsByName}, timezone=${
        timezone || 'undefined'
      }; treating as inactive`
    );
    return false;
  }

  const expressModePeriods = getExpressModePeriods(restaurantMealPeriodsByName);

  if (expressModePeriods.length === 0) {
    logger.log(
      `[EXPRESS_MODE] no meal periods with "${EXPRESS_MODE_MEAL_PERIOD_PREFIX}" prefix found for this restaurant (scanned ${
        Object.keys(restaurantMealPeriodsByName).length
      } meal periods)`
    );
    return false;
  }

  for (const mp of expressModePeriods) {
    if (isRestaurantMealPeriodActive(mp, timezone)) {
      logger.log(
        `[EXPRESS_MODE] active meal period: "${mp.name}" (timezone=${timezone})`
      );
      return true;
    }
  }

  logger.log(
    `[EXPRESS_MODE] found ${
      expressModePeriods.length
    } express-mode meal period(s) but none are active right now in timezone=${timezone}: [${expressModePeriods
      .map((mp) => `"${mp.name}"`)
      .join(', ')}]`
  );
  return false;
};
